import { useEffect, type ReactNode } from 'react';

/**
 * A right-hand drawer for create and edit forms.
 *
 * A panel rather than a modal dialog because the list behind it stays
 * readable - you can check an existing username while typing a new one.
 *
 * Two behaviours that are easy to leave out and immediately missed:
 * Escape closes it, and (in the default overlay mode) the page behind does
 * not scroll while it is open.
 */
interface SidePanelProps {
  open: boolean;
  title: string;
  description?: string;
  /** Wider drawer for content-rich panels like the task details. Ignored
   *  when `overlay` is false - an inline panel is sized by its parent. */
  wide?: boolean;
  onClose: () => void;
  children: ReactNode;
  /**
   * Default true: a fixed drawer over a dark scrim, like every create/edit
   * form in the app. Pass false for a caller that wants this panel to sit
   * in NORMAL page flow instead - alongside other content that must stay
   * fully visible and interactive next to it (the Notifications page's
   * split view is the one place that needs this today). An inline panel
   * still closes on Escape, but does not dim or lock the rest of the page,
   * since there is nothing behind it to protect.
   */
  overlay?: boolean;
  /**
   * "Light dismiss": clicking the dark scrim outside the panel closes it.
   * Default true. Pass false for panels holding a form mid-entry (New task),
   * where a stray outside click would throw away everything typed - the X
   * button and Escape still close it deliberately.
   */
  closeOnBackdropClick?: boolean;
}

export function SidePanel({
  open,
  title,
  description,
  wide,
  onClose,
  children,
  overlay = true,
  closeOnBackdropClick = true,
}: SidePanelProps) {
  useEffect(() => {
    if (!open) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }

    document.addEventListener('keydown', onKeyDown);
    if (!overlay) return () => document.removeEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose, overlay]);

  if (!open) return null;

  if (!overlay) {
    return (
      <aside
        role="region"
        aria-label={title}
        className="flex h-full min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
          <div>
            <h2 className="text-base font-semibold text-slate-900">{title}</h2>
            {description && (
              <p className="mt-1 text-sm text-slate-500">{description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="-mr-2 rounded-md p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden="true">
              <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">{children}</div>
      </aside>
    );
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      {/* Scrim. Clicking it closes ("light dismiss") - unless the caller
          opted out to protect a half-filled form from a stray click. */}
      {closeOnBackdropClick ? (
        <button
          type="button"
          aria-label="Close panel"
          onClick={onClose}
          className="absolute inset-0 cursor-default bg-slate-900/40"
        />
      ) : (
        <div aria-hidden="true" className="absolute inset-0 bg-slate-900/40" />
      )}

      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={[
          'relative z-10 flex h-full w-full flex-col border-l border-slate-200 bg-white shadow-xl',
          wide ? 'max-w-[620px]' : 'max-w-md',
        ].join(' ')}
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
          <div>
            <h2 className="text-base font-semibold text-slate-900">{title}</h2>
            {description && (
              <p className="mt-1 text-sm text-slate-500">{description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="-mr-2 rounded-md p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden="true">
              <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">{children}</div>
      </aside>
    </div>
  );
}
